import { useEffect, useRef, useState } from "react";

import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import { styled } from "@mui/material/styles";

interface CollapsibleMessageProps {
  message: string;
  label?: string;
}

const MessageContainer = styled(Box)<{
  $isCollapsed: boolean;
  $hasOverflow: boolean;
}>`
  position: relative;
  max-height: ${(props) => (props.$isCollapsed ? "3em" : "none")};
  overflow: hidden;
  white-space: pre-wrap;
  word-break: break-word;

  ${(props) =>
    props.$isCollapsed &&
    props.$hasOverflow &&
    `
    &::after {
      content: "";
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      height: 1.5em;
      background: linear-gradient(to bottom, transparent, white);
    }
  `}
`;

export const CollapsibleMessage = ({
  message,
  label = "Message",
}: CollapsibleMessageProps) => {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [hasOverflow, setHasOverflow] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const hasLengthOverflow = message.length > 200;

    if (contentRef.current) {
      const lineHeight = parseFloat(
        getComputedStyle(contentRef.current).lineHeight
      );
      const maxHeight = lineHeight * 3;
      const actualHeight = contentRef.current.scrollHeight;
      const hasLineOverflow = actualHeight > maxHeight;

      setHasOverflow(hasLengthOverflow || hasLineOverflow);
    } else {
      setHasOverflow(hasLengthOverflow);
    }
  }, [message]);

  const handleToggle = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsCollapsed(!isCollapsed);
  };

  return (
    <Box
      sx={{
        fontSize: "14px",
        color: "gray",
        opacity: 0.7,
      }}
    >
      <Box>
        <MessageContainer
          ref={contentRef}
          component="span"
          sx={{ display: "inline-block" }}
          $isCollapsed={isCollapsed && hasOverflow}
          $hasOverflow={hasOverflow}
        >
          <strong>{label}:</strong> {message}
        </MessageContainer>
      </Box>
      {hasOverflow && (
        <Link
          href="#"
          onClick={handleToggle}
          sx={{
            cursor: "pointer",
            display: "inline-block",
          }}
        >
          {isCollapsed ? "more" : "less"}
        </Link>
      )}
    </Box>
  );
};
